// Near-field grass: one InstancedMesh of crossed-quad tufts scattered
// DETERMINISTICALLY (world-cell hash) in a ring around the player, so the
// same tuft always grows in the same spot regardless of approach direction.
// Pure decoration — no collision, no gameplay. ~1 draw call, ≤6k tris,
// gentle vertex-shader wind. Placement rules: gentle slopes, below the
// alpine turf line, not in a lake, not inside a building footprint.
import * as THREE from 'three';
import { corridorCovAt, corridorCarved, corridorGen } from './corridor.js';
import {
  BUSH_VAR, BUSH_ALPHA, TUFT_ALPHA, TUFT_VAR, FLOWER_ALPHA,
  BUSH_ASPECT,
} from './vegpaint.js';
// the sprite look hooks + the atlases painted in the pool: vegmat.js (shared
// with the dashboard's mini forest); re-exported for this module's importers
import {
  vegLook, vegOwnLook, vegOwnState, vegSpriteUrl, setSpritePage, poolAtlas, atlasSprite, disposeAtlas,
  vegAlphaGLSL, vegCoverage, vegCoverageDecl,
} from './vegmat.js';
export { vegLook, vegOwnLook, vegOwnState, VEG_SPRITE_LOOK, setVegSpriteLook } from './vegmat.js';
import { run as poolRun } from './pool.js';
import { fetchAsset } from './assetq.js';
import { sunDirFromLighting } from './scenelight.js';
import { makeClumpGeometry, makeStoneMaterial, stoneGround, stoneCentre, CLUMP_STONES, poolGLSL, POOL_UNIFORMS } from './stones.js';
import { loadRough, roughAt, stoneTune } from './rough.js';
import { genLive, onGenChange } from './gensched.js';
import RENDER_CFG from './renderconfig.json' with { type: 'json' };   // node-loadable (check-draw imports grass.js)

// GENSURF (gs-family-grass): the scatter ring (radius_m), the instance counts (tufts / bushes / stones /
// flowers) are tunables gensurf by_class.<class>.grass
// (genLive: clamped to renderconfig gensurf, the ceilings). Every mesh is ALLOCATED at GRASS_CEIL, the
// largest ceiling of any class (the device class is not known when the meshes are made), and draws the
// live counts; a change re-scatters. Before the tunables land (and in node) the ceilings draw. The desktop
// grass shells are removed (owner 2026-10-27: they smeared way edges near the player); renderconfig still
// carries their retired shells / shell_m ceilings (a bake-id term) until the next re-key.
const GRASS_KEYS = ['radius_m', 'tufts', 'bushes', 'stones', 'flowers'];
export const GRASS_CEIL = Object.fromEntries(GRASS_KEYS.map((k) =>
  [k, Math.max(...Object.values(RENDER_CFG.gensurf.by_class).map((c) => c.grass[k]))]));
const GRASS_IDLE = { ...GRASS_CEIL };
const STEP = 1.6;         // one candidate cell per STEP x STEP m
const REBUILD_DIST = 6;   // re-scatter after this much movement
// the scatter's per-cell answers (height, normal, class, every blocking test)
// are cached between re-scatters: a 6 m move re-derives only the ring's new
// leading edge (108-130 of the disc's ~830 cells). Dropped whole only when a
// source changes (cellKey, the corridor registrations included); past
// CELL_CAP the cells beyond radius_m + EVICT_PAD_M go. A cell whose ground / class / ways tile
// is not in yet is never cached (asked again RETRY_MS later). It once was
// cached as "nothing grows" and wiped every 2 s instead, so nearly every
// re-scatter while walking was cold: the whole disc in one frame (8-46 ms,
// the moving hitch). At most COLD_CELLS new cells per frame; the rest the
// next frames (the leading edge, where the edge fade keeps tufts small).
const CELL_CAP = 8192;
export const GRASS_COLD_CELLS = 160;
// the time bound (quest-move 2026-11-02 on the quest: a fast flight
// re-scatters every ~5 frames, and 160 cold cells were a 10-16 ms frame
// there; every class since all-res 2026-11-02 — a pure slice, the same cells
// later): past the first GRASS_COLD_MIN cold cells of a re-scatter it also
// stops once the update has run GRASS_COLD_MS — the rest are owed to the next
// frames as above.
export const GRASS_COLD_MS = 1.0;
export const GRASS_COLD_MIN = 24;
const EVICT_PAD_M = 8;    // cells beyond radius_m + this go past CELL_CAP
const RETRY_MS = 1000;
const STONE_MIN_NY = 0.5; // no stone on a face steeper than ~60 deg (it would not stay)
// the tufts' measured colour: the GROUND-M pool at the tuft's foot, the mip of ~GRASS_TEXEL_M
const GRASS_TEXEL_M = 0.5;
// Fallbacks only — setGrassBands() replaces both from the bake's meta.bands /
// water.json. ALT_MAX was the Upper Engadin turf->scree line and LAKE_Y the
// surveyed St. Moritzersee level, hardcoded into the client.
let ALT_MAX = 2600;       // above this the turf gives way to scree
let LAKE_Y = 1768.8;      // pre-WaterIndex fallback: suppress below this level

// SHRUBS (owner 2026-10-18, verbatim: "default altitude limit for scrubs to
// 2400m"): no bush (grass.js) nor measured shrub (shrubs.js) above the
// world's cap — its vegetation.shrubs.max_elev_m (per world, in bake_id),
// else SHRUB_CAP_DEFAULT_M. The bands carry no shrub line of their own.
export const SHRUB_CAP_DEFAULT_M = 2400;
export function shrubCapM(veg) {
  const m = veg && veg.shrubs && veg.shrubs.max_elev_m;
  return Number.isFinite(m) && m > 0 ? m : SHRUB_CAP_DEFAULT_M;
}

export function setGrassBands(bands, lakes) {
  if (bands && Array.isArray(bands.turf_out_m)) {
    ALT_MAX = (bands.turf_out_m[0] + bands.turf_out_m[1]) / 2;
  }
  // the real water level for THIS world: the lowest lake plane the bake found,
  // +0.9 m so tufts do not sprout out of the sheet
  const ys = (lakes || []).map((l) => l.level).filter(Number.isFinite);
  if (ys.length) LAKE_Y = Math.min(...ys) + 0.9;
  else if (bands && Array.isArray(bands.lake_m)) LAKE_Y = bands.lake_m[0];
}

// per-class scatter profile: densities are %-rolls per candidate cell.
// g = grass tufts, b = bushes, r = stone clumps (rc = stones in a clump, max
// CLUMP_STONES; default 2: a lone stone or a pair); gs/bs/rs = size multipliers.
// Classes absent here fall back to the elevation heuristics (also the
// no-classmap path). A tuft's colour is the measured ground's (GRASS_TEXEL_M).
const CLASS_SCATTER = {
  0:  { g: 74, b: 2, r: 1, f: 30 },                          // meadow: lush + flowers
  1:  { g: 52, b: 4, r: 3, f: 10 },                          // turf: drier
  2:  { g: 26, b: 26, r: 3, bs: 1.15 },                      // heath: shrubby
  3:  { g: 34, b: 12, r: 2, gs: 1.25 },                     // forest: ferns/undergrowth
  4:  { g: 4, b: 0, r: 13, rs: 1.25, rc: 5 },                // scree: a talus carpet
  5:  { g: 2, b: 0, r: 9, rs: 1.15, rc: 4 },                 // rock
  6:  { g: 3, b: 0, r: 5, rs: 0.8, rc: 3 },                  // gravel/riverbed
  7:  { g: 60, b: 3, r: 0, gs: 2.1 },                      // wetland: reeds
  8:  { g: 40, b: 0, r: 0, gs: 0.6 },                        // sport: short turf
  9:  { g: 48, b: 3, r: 0, gs: 0.75, f: 8 },                 // lawn
  10: { g: 55, b: 1, r: 1, f: 6 },                           // piste corridor
  11: { g: 6, b: 0, r: 4 },                                  // dirt
  12: { g: 0, b: 0, r: 0 },                                  // asphalt
  13: { g: 0, b: 0, r: 0 },                                  // paved
  14: { g: 2, b: 0, r: 6, rs: 0.6, rc: 3 },                  // ballast
  15: { g: 0, b: 0, r: 2 },                                  // snow
  254: { g: 0, b: 0, r: 0 },                                 // water
};

// WAYS-G (owner 2026-10-12, verbatim: "no plants or rocks on streets or on
// sidewalks or in covered places"): no scatter (tufts, bushes, flowers,
// rocks) on a drawn ground way, its sidewalk / island / ring apron (the bake's
// ways field, surface.js tileData().ways: P0.R < r_off = inside the drawn
// union, walks included) or within WAY_CLEAR_M of its edge (the kerb; the
// classmap's speckle along a drawn edge), nor on a classmap cell whose 5 x 5
// neighbourhood is mostly paved (PAVED_CLASSES: an off-way speckle in a paved
// place). A bake that ships ways tiles but whose tile has not landed yet
// answers "blocked" (surface.onWays re-scatters). GRASS_PROBE.ways_off:
// check-draw's red twin only (the pre-2026-10-12 scatter), never set in the app.
export const WAY_CLEAR_M = 1.0;
export const PAVED_CLASSES = [12, 13];   // asphalt, paved (CLASS_SCATTER: nothing grows)
const PAVED_MAJORITY = 13;               // of the 25 cells of the 5 x 5 neighbourhood
export const GRASS_PROBE = { ways_off: false };

// true = nothing of the scatter at (x, z): on / near a way, or paved ground
export function scatterBlocked(surface, x, z) {
  if (GRASS_PROBE.ways_off || !surface) return false;
  const P = surface.ways;
  if (P && surface.tileData) {
    const tm = P.tile_m, cm = P.cell_m;
    const tx = Math.floor(x / tm), ty = Math.floor(z / tm);
    const d = surface.tileData(tx, ty);
    if (!d || !d.ways) return true;   // not landed: no answer yet, nothing grows
    const T = d.ppx;
    const cx = Math.min(T - 1, Math.max(0, Math.floor((x - tx * tm) / cm)));
    const cz = Math.min(T - 1, Math.max(0, Math.floor((z - ty * tm) / cm)));
    const R = d.ways[(cz * T + cx) * 4];
    const E = P.enc && typeof P.enc === 'object' ? P.enc : {};
    const off = Number.isFinite(E.r_off) ? E.r_off : 128, sc = Number.isFinite(E.r_scale) ? E.r_scale : 32;
    if (R !== 255 && R < off + sc * WAY_CLEAR_M) return true;
  }
  if (surface.classAt) {
    const c = surface.meta && surface.meta.cell_m > 0 ? surface.meta.cell_m : 1;
    let n = 0;
    for (let j = -2; j <= 2; j++) {
      for (let i = -2; i <= 2; i++) if (PAVED_CLASSES.includes(surface.classAt(x + i * c, z + j * c))) n++;
    }
    if (n >= PAVED_MAJORITY) return true;
  }
  return false;
}

// "covered places" (the same owner line): the ways field carries ground ways
// only, so the ground UNDER a deck (a bridge, a viaduct, a covered
// walkway's deck — ways.json decks[], every kind) is a hit for the scatter,
// the trees and the shrubs: the deck's footprint, station by station from
// its own edges (x, z +- n * eL / eR, the top deckRibbons clips to), grown
// by `pad` (WAY_CLEAR_M) across and at both ends. -> hitFn(x, z) (true:
// nothing grows), or null with no decks. Indexed on an 8 m grid.
export function deckCover(decks, pad = WAY_CLEAR_M) {
  const CELL = 8, grid = new Map(), quads = [];
  for (const d of decks || []) {
    const n = Array.isArray(d.x) ? d.x.length : 0;
    if (n < 2 || !d.nx || !d.eL || !d.eR) continue;
    for (let j = 0; j + 1 < n; j++) {
      const tx = d.x[j + 1] - d.x[j], tz = d.z[j + 1] - d.z[j], L = Math.hypot(tx, tz);
      if (!(L > 0)) continue;
      const ux = tx / L, uz = tz / L;
      const e0 = j === 0 ? pad : 0, e1 = j + 2 === n ? pad : 0;   // the deck's two ends
      const side = (k, s, e) => {
        const w = s < 0 ? d.eL[k] + pad : d.eR[k] + pad, a = k === j ? -e : e;
        return [d.x[k] + s * d.nx[k] * w + ux * a, d.z[k] + s * d.nz[k] * w + uz * a];
      };
      const V = [side(j, -1, e0), side(j, 1, e0), side(j + 1, 1, e1), side(j + 1, -1, e1)];
      const id = quads.length;
      quads.push(V);
      const xs = V.map((v) => v[0]), zs = V.map((v) => v[1]);
      for (let cx = Math.floor(Math.min(...xs) / CELL); cx <= Math.floor(Math.max(...xs) / CELL); cx++) {
        for (let cz = Math.floor(Math.min(...zs) / CELL); cz <= Math.floor(Math.max(...zs) / CELL); cz++) {
          const k = cx + '_' + cz;
          let a = grid.get(k);
          if (!a) grid.set(k, a = []);
          a.push(id);
        }
      }
    }
  }
  if (!quads.length) return null;
  return (x, z) => {
    if (GRASS_PROBE.ways_off) return false;
    const ids = grid.get(Math.floor(x / CELL) + '_' + Math.floor(z / CELL));
    if (!ids) return false;
    for (const id of ids) {
      const V = quads[id];
      let c = false;
      for (let i = 0, k = 3; i < 4; k = i++) {
        const [xi, zi] = V[i], [xk, zk] = V[k];
        if ((zi > z) !== (zk > z) && x < ((xk - xi) * (z - zi)) / (zk - zi) + xi) c = !c;
      }
      if (c) return true;
    }
    return false;
  };
}

export class Grass {
  constructor(scene, hf, collider, baseUrl = null) {
    this.hf = hf;
    this.collider = collider;
    this.baseUrl = baseUrl;      // the owner's shrub sprites: veg/<sha>.webp
    this._spriteGen = 0;
    this._cells = new Map();     // cell key -> CELL_NONE | {y, ny, cid} (_cell)
    this._cellKey = null;        // the sources the cached answers were read from
    this._cold = 0;              // new cells this re-scatter may still derive (_cell)
    this._coldEnd = Infinity;    // and until when (the quest's time bound; update)
    this._owed = false;          // the last re-scatter hit GRASS_COLD_CELLS: finish next frame
    this._pendAt = 0;            // a cell's tile was not in: re-scatter at this time
    this.roads = null;   // set by main once the roads mask loads
    this.surface = null; // classmap sampler (surface.js), set by main (setter below)
    this.last = new THREE.Vector3(1e9, 0, 0);
    this.time = { value: 0 };
    this.vt = null;              // the GROUND-M pool (setGround): the tufts' colour
    this.gl = GRASS_IDLE;        // the live grass block (genLive; _liveGrass)
    // the tufts: the measured ground colour at the foot (the pool; no tile there: not drawn) x the blade
    // shading = the painted texel's luminance over its column's mean (tuftLum: mean 1) x a tone (mean 1),
    // x W like the ground (setGround); radiance, so no vegLook. The atlases (tufts, bushes, flowers) are
    // painted in the client worker pool (vegmat.js poolAtlas); each material draws once its atlas landed.
    this.run = poolRun;
    const tuft = makeTuftAtlas(this.run);
    this._tuftL = { value: new Array(TUFT_VAR).fill(1) };
    const mat = new THREE.MeshBasicMaterial({
      map: tuft.tex, alphaTest: TUFT_ALPHA, side: THREE.DoubleSide,
      fog: true, color: 0xffffff,
    });
    mat.visible = false;
    tuft.ready.then(() => { this._tuftL.value = tuft.lum; mat.visible = true; }, (err) => console.warn('[veg atlas]', err));
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.time;
      shader.uniforms.uTuftL = this._tuftL;
      const pool = this._poolDecl(shader, 'grGround', GRASS_TEXEL_M);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uTime;\nattribute float aVar;\nvarying vec3 vGrG;\nvarying float vGrVar;\n' + pool)
        .replace('#include <uv_vertex>',
          `#include <uv_vertex>
          vMapUv.x = (vMapUv.x + aVar) / ${TUFT_VAR}.0;`)
        .replace('#include <begin_vertex>',
          `#include <begin_vertex>
          // sway the blade tips only (uv.y = 1 at the top)
          float sw = uv.y * uv.y * 0.05;
          transformed.x += sin(uTime * 1.7 + position.x * 3.1 + position.z * 2.3) * sw;
          transformed.z += cos(uTime * 1.3 + position.x * 2.7) * sw;
          #ifdef GRASS_VT
          vGrG = grGround((modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xz);
          #else
          vGrG = vec3(-1.0);
          #endif
          vGrVar = aVar;
          if (vGrG.r < 0.0) transformed = vec3(0.0);`);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>\nvarying vec3 vGrG;\nvarying float vGrVar;\nuniform float uTuftL[${TUFT_VAR}];`)
        .replace('#include <map_fragment>',
          `vec4 tg = texture2D(map, vMapUv);
          diffuseColor.rgb *= vGrG * dot(tg.rgb, vec3(0.2126, 0.7152, 0.0722)) / uTuftL[int(vGrVar + 0.5)];
          diffuseColor.a *= tg.a;`);
      sharpenAlpha(shader, mat, null, false);
    };
    vegCoverage(mat);
    mat.customProgramCacheKey = () => 'grass-wind' + TUFT_VAR + this._poolKey() + ':a2c';
    // the tuft shape per instance (atlas column, vegpaint.js TUFT_VAR): from
    // the surface class and dryness at scatter time (tuftVar)
    const tuftGeo = makeTuftGeometry();
    this.tuftVar = new THREE.InstancedBufferAttribute(new Float32Array(GRASS_CEIL.tufts), 1);
    tuftGeo.setAttribute('aVar', this.tuftVar);
    this.mesh = new THREE.InstancedMesh(tuftGeo, mat, GRASS_CEIL.tufts);
    // the tone buffer (setColorAt, _scatter) from the start: three keys the
    // program on instanceColor, and setColorAt made it lazily at the first
    // scatter — the warm-up (count 0, before it) compiled the variant without
    // it, and the first scatter built 'grass-wind' inside a frame. The same
    // buffer setColorAt makes (all 1); every drawn instance writes its own.
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(GRASS_CEIL.tufts * 3).fill(1), 3);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;   // always around the camera anyway
    this.mesh.name = 'grass';
    this.mesh.matrixAutoUpdate = false;   // static (all four sets): the instances carry the placement
    scene.add(this.mesh);
    // bushes: crossed quads, 2-variant atlas — column 0 = leafy shrub
    // (alpenrose-style, optional bloom sprinkle), column 1 = NEEDLE shrub
    // (dwarf pine). The regional vegetation profile picks colors + how many
    // shrubs are coniferous (shrubs.needle_share); per-instance aVar selects
    // the column. Gentle wind like the tufts, a touch stiffer.
    this._shrubStyle = { color: '#3e6234', bloom: '#c95a74', needle: 0.45 };
    const bush0 = poolAtlas(this.run, 'bush', { color: this._shrubStyle.color, bloom: this._shrubStyle.bloom, S: 64 });
    const bushMat = new THREE.MeshBasicMaterial({
      map: bush0.tex,
      alphaTest: BUSH_ALPHA, side: THREE.DoubleSide, fog: true,
    });
    bushMat.visible = false;
    bushMat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.time;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uTime;\nattribute float aVar;')
        .replace('#include <uv_vertex>',
          `#include <uv_vertex>
          vMapUv.x = (vMapUv.x + aVar) / ${BUSH_VAR}.0;`)
        .replace('#include <begin_vertex>',
          `#include <begin_vertex>
          float sw = uv.y * uv.y * 0.03;
          transformed.x += sin(uTime * 1.2 + position.x * 2.1) * sw;
          transformed.z += cos(uTime * 0.9 + position.z * 1.7) * sw;`);
      sharpenAlpha(shader, bushMat, bushMat.userData.vegOwn);
    };
    bushMat.userData.vegOwn = vegOwnState();
    vegCoverage(bushMat);
    bushMat.customProgramCacheKey = () => 'bush-wind' + BUSH_VAR + ':a2c';
    wearBushAtlas(this, bushMat, bush0, null);
    const bushGeo = makeTuftGeometry();
    this.bushVar = new THREE.InstancedBufferAttribute(new Float32Array(GRASS_CEIL.bushes), 1);
    bushGeo.setAttribute('aVar', this.bushVar);
    this.bushes = new THREE.InstancedMesh(bushGeo, bushMat, GRASS_CEIL.bushes);
    this.bushes.count = 0;
    this.bushes.frustumCulled = false;
    this.bushes.matrixAutoUpdate = false;
    scene.add(this.bushes);
    // stones: clumps of chunky rocks lit like the ground (stones.js; the
    // light + the ground's colour come with setGround)
    const stoneGeo = makeClumpGeometry();
    const C = GRASS_CEIL.stones;
    this.stoneInst = new THREE.InstancedBufferAttribute(new Float32Array(C * 4), 4);
    this.stoneNg = new THREE.InstancedBufferAttribute(new Float32Array(C * 3), 3);
    stoneGeo.setAttribute('aInst', this.stoneInst);
    stoneGeo.setAttribute('aNg', this.stoneNg);
    this.rocks = new THREE.InstancedMesh(stoneGeo, makeStoneMaterial(), C);
    this.rocks.count = 0;
    this.rocks.frustumCulled = false;
    this.rocks.name = 'stones';
    this.rocks.matrixAutoUpdate = false;
    scene.add(this.rocks);
    this._clsKey = new Map();    // class id -> key (meta.surface.classes: rough.js's host classes)
    this.rough = null;           // the measured roughness grid (rough.js; null = the phase-1 stones)
    // flowers: mixed-bouquet crossed quads, CLUSTERED via a coarse world-cell
    // hash so they read as flower patches, not confetti
    const flower = poolAtlas(this.run, 'flower');
    const flowerMat = new THREE.MeshBasicMaterial({
      map: flower.tex, alphaTest: FLOWER_ALPHA, side: THREE.DoubleSide, fog: true,
    });
    flowerMat.visible = false;
    flower.ready.then(() => { flowerMat.visible = true; }, (err) => console.warn('[veg atlas]', err));
    flowerMat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.time;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uTime;')
        .replace('#include <begin_vertex>',
          `#include <begin_vertex>
          float sw = uv.y * uv.y * 0.04;
          transformed.x += sin(uTime * 1.5 + position.x * 2.9) * sw;`);
      sharpenAlpha(shader, flowerMat);
    };
    vegCoverage(flowerMat);
    flowerMat.customProgramCacheKey = () => 'flower-wind:a2c';
    this.flowers = new THREE.InstancedMesh(makeTuftGeometry(), flowerMat, GRASS_CEIL.flowers);
    this.flowers.count = 0;
    this.flowers.frustumCulled = false;
    this.flowers.matrixAutoUpdate = false;
    scene.add(this.flowers);
    // a live grass value moved: re-scatter (its kind is 'uniform', gensched HOT)
    onGenChange(() => { this.last.set(1e9, 0, 0); });
    this._M = new THREE.Matrix4();
    this._Q = new THREE.Quaternion();
    this._S = new THREE.Vector3();
    this._P = new THREE.Vector3();
    this._C = new THREE.Color();
  }

  // R8h2 world swap (worldswap.js vegHook): the new meta.vegetation, whose
  // sprite references alone moved — the shrubs block's sprites go onto the
  // bushes' atlas (or the one about to land), as applyVegetation's
  // wearBushAtlas does at boot
  reloadSprites(veg) {
    this.veg = veg || null;
    if (!(veg && veg.shrubs)) return;
    const mat = this.bushes.material;
    loadBushSprites(this, mat, mat.userData.atlasWant, this.baseUrl, veg.shrubs);
  }

  // restyle from the bake's vegetation block (meta.vegetation)
  applyVegetation(veg) {
    this.veg = veg || null;
    this._grassMax = (veg && veg.grass && veg.grass.max_elev_m) || ALT_MAX;
    this._bushMax = shrubCapM(veg);
    if (veg && veg.shrubs) {
      this._bushMin = veg.shrubs.min_elev_m || 2200;
      this._bushDens = veg.shrubs.density === undefined ? 1 : veg.shrubs.density;
      // regional shrub LOOK: repaint the leafy/needle atlas with the
      // profile's foliage + bloom colors (bloom omitted = no flowers)
      const st = {
        color: /^#[0-9a-f]{6}$/i.test(veg.shrubs.color || '')
          ? veg.shrubs.color : this._shrubStyle.color,
        bloom: /^#[0-9a-f]{6}$/i.test(veg.shrubs.bloom || '') ? veg.shrubs.bloom : null,
        needle: Number.isFinite(veg.shrubs.needle_share)
          ? Math.min(1, Math.max(0, veg.shrubs.needle_share)) : this._shrubStyle.needle,
      };
      this._shrubStyle = st;
      wearBushAtlas(this, this.bushes.material, poolAtlas(this.run, 'bush', { color: st.color, bloom: st.bloom, S: 64 }), veg.shrubs);
    }
    this.last.set(1e9, 0, 0);          // re-scatter with the new look
  }

  // the bake's light model + the ground's colour for the stones and the tufts
  // (main.js, after the terrain): meta.tex.light / meta.lighting and
  // the GROUND-M texture pool (terrain.gm.vt; without it none of them draws)
  setGround(meta, terrain) {
    const vt = terrain && terrain.gm && terrain.gm.vt ? terrain.gm.vt : null;
    stoneGround(this.rocks.material, meta, vt, sunDirFromLighting(meta.lighting));
    this.vt = vt && vt.uniforms ? vt : null;
    this.mesh.material.color.setScalar(meta.tex.light.white);   // linear: the terrain's gain
    this.mesh.material.needsUpdate = true;
    this._clsKey.clear();
    for (const c of (meta.surface && meta.surface.classes) || []) this._clsKey.set(c.id, c.key);
    this._cells.clear();               // the stones' cached light / colour
    this.last.set(1e9, 0, 0);
    // S4: the measured roughness (meta.surface.rough) sizes the stones per area
    loadRough(this.baseUrl, meta).then((r) => this.setRough(r), (err) => console.warn('[rough]', err));
  }

  // the roughness grid (rough.js loadRough; null = none: the phase-1 stones)
  setRough(R) {
    if (R === this.rough) return;
    this.rough = R;
    this._cells.clear();               // the stones' cached tune / pose
    this.last.set(1e9, 0, 0);
  }

  // Forest/Shrubs.exclude contract: hitFn(x, z) -> true = nothing grows here
  // (main.js: the emitted bores' floors). Accumulates.
  exclude(hitFn) {
    if (!hitFn) return;
    (this.exclFns || (this.exclFns = [])).push(hitFn);
    this.last.set(1e9, 0, 0);       // re-scatter
    this._cells.clear();
  }

  // the classmap streams in 512 m tiles (surface.js ensure): a tile landing
  // after the last scatter re-scatters; its cells were never cached
  // (surfaceIn), so the cache stays (a landing anywhere in the window used to
  // wipe it: one more cold disc)
  get surface() { return this._surface; }
  set surface(s) {
    this._surface = s;
    this._cells.clear();
    if (s && s.onTile) s.onTile(() => { this.last.set(1e9, 0, 0); });
    // a ways field tile landing re-scatters too (scatterBlocked: no answer
    // before it, nothing grows)
    if (s && s.onWays) s.onWays(() => { this.last.set(1e9, 0, 0); });
  }

  // the sources a cell's answer reads (an identity / generation each)
  _cellSources() {
    const st = this._srcIds || (this._srcIds = new Map());
    const id = (o) => { if (!o) return 0; let v = st.get(o); if (!v) st.set(o, v = st.size + 1); return v; };
    return `${id(this.water)}|${id(this.rivers)}|${id(this.roads)}|${id(this.surface)}|${this.collider ? this.collider.gen : -1}`
      + `|${this.exclFns ? this.exclFns.length : 0}|${corridorGen()}`;
  }

  // a candidate cell's position-only answer: CELL_NONE (nothing may grow) or
  // {y, ny (the normal's y), cid (class id; -1 = no classmap)}; cached unless
  // its tiles are not in yet (no height / normal, no class / ways tile: asked
  // again RETRY_MS later); CELL_OWED = not derived, this frame's budget is spent
  _cell(gx, gz, x, z) {
    const key = gx * 1048576 + gz;
    const had = this._cells.get(key);
    if (had !== undefined) return had;
    if (this._cold <= 0) return CELL_OWED;
    if (this._cold <= GRASS_COLD_CELLS - GRASS_COLD_MIN && performance.now() >= this._coldEnd) { this._cold = 0; return CELL_OWED; }
    this._cold--;
    const y = this.surfaceIn(x, z) ? this.hf.height(x, z) : null;
    if (y === null) { this._pend = true; return CELL_NONE; }
    let c = CELL_NONE;
    if (this._grows(x, y, z)) {
      const nrm = this.hf.normal(x, z, _n);
      if (!nrm) { this._pend = true; return CELL_NONE; }
      c = { y, nx: nrm.x, ny: nrm.y, nz: nrm.z, cid: this.surface ? (this.surface.classAt(x, z) ?? 255) : -1 };
    }
    this._cells.set(key, c);
    return c;
  }

  // the classmap (+ ways) tile under (x, z) is resident — or there is none to
  // wait for (no classmap, outside its grid). classAt reads a missing tile as
  // the none class and scatterBlocked a missing ways tile as blocked: an
  // answer from either is not cached.
  surfaceIn(x, z) {
    const S = this.surface, T = S && S.meta && S.meta.tiles;
    if (!S || !S.tileData || !T || !(T.tile_m > 0)) return true;
    const tx = Math.floor(x / T.tile_m), ty = Math.floor(z / T.tile_m);
    if (tx < 0 || ty < 0 || (T.nx > 0 && tx >= T.nx) || (T.ny > 0 && ty >= T.ny)) return true;
    const d = S.tileData(tx, ty);
    return !!d && (!S.ways || !!d.ways);
  }

  // the pool read for a material (stones.js poolGLSL as fn; '' without a pool:
  // GRASS_VT undefined, nothing drawn) and its program key
  _poolDecl(shader, fn, texelM) {
    if (!this.vt) return '';
    const vu = this.vt.uniforms;
    for (const k of POOL_UNIFORMS) shader.uniforms[k] = vu[k];
    return '#define GRASS_VT\n' + poolGLSL(vu.uVtLv.value.length, texelM, fn);
  }
  _poolKey() { return this.vt ? 'vt' + this.vt.uniforms.uVtLv.value.length : ''; }

  // the live grass block (genLive: clamped to the ceilings); GRASS_IDLE before the tunables land
  _liveGrass() {
    const l = genLive();
    return (l && l.grass) || GRASS_IDLE;
  }

  // past CELL_CAP: drop the cells beyond radius_m + EVICT_PAD_M of pos (never the disc's)
  _evict(pos) {
    const r = (this.gl.radius_m + EVICT_PAD_M) / STEP;
    const px = pos.x / STEP, pz = pos.z / STEP;
    for (const key of this._cells.keys()) {
      const gx = Math.floor(key / 1048576), gz = key - gx * 1048576;
      if (Math.abs(gx - px) > r || Math.abs(gz - pz) > r) this._cells.delete(key);
    }
  }

  // every blocking test of a candidate point (water, river, house, street,
  // paved ground, a carved corridor, an exclusion)
  _grows(x, y, z) {
    // Nothing grows on water. Point-in-polygon against the real lake
    // sheets (this.water = the shared WaterIndex): a multi-lake world
    // broke the single LAKE_Y waterline both ways — every lake ABOVE the
    // lowest one sprouted tufts out of its sheet, and dry valleys BELOW
    // it were sterilised. LAKE_Y survives only as the fallback while
    // water.json hasn't loaded. Rivers are walkable riverbeds, but no
    // reed grows mid-stream either (RiverIndex over the drawn ribbons).
    if (this.water) {
      if (this.water.surfaceAt(x, z) !== null) return false;
    } else if (y < LAKE_Y) return false;
    if (this.rivers && this.rivers.wet(x, z)) return false;
    if (this.collider && this.collider.insideFootprint(x, z)) return false;
    if (this.roads && this.roads.at(x, z) > 0.35) return false;   // nothing on streets
    // nor on a sidewalk / island / apron, the kerb, paved ground (WAYS-G)
    if (scatterBlocked(this.surface, x, z)) return false;
    // nothing on ground that the corridor/bore carve removed — but the
    // MEADOW ABOVE a bore shares that xz footprint and is ordinary ground
    if (corridorCovAt(x, z) > 0.4 && corridorCarved(x, y, z)) return false;
    // station halls have no collider footprint (walk-in shells) — keep
    // the scatter out of their interiors via exclusion circles
    if (this.exclFns && this.exclFns.some((f) => f(x, z))) return false;
    return true;
  }

  update(pos, dt) {
    this.time.value += dt;
    const now = performance.now();
    const due = this._owed || (this._pendAt > 0 && now >= this._pendAt);
    if (!due && pos.distanceTo(this.last) < REBUILD_DIST) return;
    this.last.copy(pos);
    const L = this.gl = this._liveGrass(), RADIUS = L.radius_m;
    const ck = this._cellSources();
    if (ck !== this._cellKey) {
      this._cells.clear();
      this._cellKey = ck;
    } else if (this._cells.size > CELL_CAP) this._evict(pos);
    this._cold = GRASS_COLD_CELLS;
    this._coldEnd = now + GRASS_COLD_MS;
    this._owed = false;
    this._pend = false;
    const g0x = Math.floor((pos.x - RADIUS) / STEP), g1x = Math.floor((pos.x + RADIUS) / STEP);
    const g0z = Math.floor((pos.z - RADIUS) / STEP), g1z = Math.floor((pos.z + RADIUS) / STEP);
    let n = 0, nb = 0, nr = 0, nf = 0;
    const up = _up;
    const MAX = L.tufts, MAX_B = L.bushes, MAX_C = L.stones, MAX_F = L.flowers;
    for (let gz = g0z; gz <= g1z && n < MAX; gz++) {
      for (let gx = g0x; gx <= g1x && n < MAX; gx++) {
        const hsh = ((gx * 73856093) ^ (gz * 19349663)) >>> 0;
        const roll = hsh % 100;
        const x = gx * STEP + ((hsh >>> 7) % 100) / 100 * STEP;
        const z = gz * STEP + ((hsh >>> 15) % 100) / 100 * STEP;
        const d = Math.hypot(x - pos.x, z - pos.z);
        if (d > RADIUS) continue;
        const c = this._cell(gx, gz, x, z);
        if (c === CELL_OWED) { this._owed = true; continue; }
        if (c === CELL_NONE) continue;
        const y = c.y, ny = c.ny;         // ny: the ground normal's y
        const edge = Math.min(1, (RADIUS - d) / 6);
        const gMax = this._grassMax || ALT_MAX;
        const dry = Math.min(1, Math.max(0, (y - (gMax - 400)) / 400));
        // ---- surface class drives the scatter mix when the bake has a
        // classmap; elevation heuristics are the fallback
        const prof = c.cid >= 0 ? CLASS_SCATTER[c.cid] : null;
        // grass: density falls with altitude; none on rock-steep ground
        const gDens = prof ? (ny >= 0.6 ? prof.g : prof.g * 0.3)
          : (y < gMax && ny >= 0.72 ? 62 * (1 - dry * 0.6) : 0);
        if (roll < gDens && n < MAX) {
          const gs = (prof && prof.gs) || 1;
          const s = (0.16 + ((hsh >>> 22) % 100) / 100 * 0.24) * gs;
          this._Q.setFromAxisAngle(up, (hsh % 63) / 63 * Math.PI);
          this._S.set(s * 1.5, s * edge, s * 1.5);
          this._P.set(x, y - 0.02, z);
          this._M.compose(this._P, this._Q, this._S);
          this.mesh.setMatrixAt(n, this._M);
          // the tuft's tone (mean 1) on the measured ground colour (the shader's pool read)
          this._C.setScalar(0.9 + ((hsh >>> 11) % 100) / 100 * 0.2);
          this.mesh.setColorAt(n, this._C);
          this.tuftVar.setX(n, tuftVar(c.cid, dry, hsh));
          n++;
        }
        // bushes: class-driven (heath/forest undergrowth), else the
        // alpenrose/dwarf-pine treeline band; never above the shrub cap
        const bMax = this._bushMax || SHRUB_CAP_DEFAULT_M;
        const bMin = this._bushMin || Math.min(ALT_MAX - 350, bMax - 200);
        // measured shrub layer present (shrubs.bin, rev >= 66): the real
        // hedges are drawn by shrubs.js — keep only a light ground garnish
        const bMul = (this._bushDens === undefined ? 1 : this._bushDens)
          * (this.shrubLayer ? 0.35 : 1);
        const bDens = y > bMax ? 0 : (prof ? (ny >= 0.55 ? prof.b : 0)
          : (ny >= 0.6 ? (y < bMin ? 3 : 9) : 0))
          * bMul;
        if ((hsh >>> 5) % 100 < bDens && nb < MAX_B) {
          const bs = (prof && prof.bs) || 1;
          const s = (0.5 + ((hsh >>> 18) % 100) / 100 * 0.8) * bs;
          this._Q.setFromAxisAngle(up, (hsh % 47) / 47 * Math.PI);
          this._S.set(s * BUSH_ASPECT, s * edge, s * BUSH_ASPECT);
          this._P.set(x, y - 0.05, z);
          this._M.compose(this._P, this._Q, this._S);
          this.bushes.setMatrixAt(nb, this._M);
          // leafy vs NEEDLE shrub: the profile's needle_share, damped in
          // forest understory (class 3 — dwarf pine belongs at the treeline)
          const cid = c.cid >= 0 ? c.cid : 255;
          let ns = this._shrubStyle.needle;
          if (cid === 3) ns *= 0.3;
          const roll2 = ((Math.imul(hsh, 2654435761) >>> 8) % 1000) / 1000;
          this.bushVar.setX(nb, roll2 < ns ? 1 : 0);
          nb++;
        }
        // flowers: only where the class has an f density, and mostly inside
        // cluster cells (9.6 m coarse hash) — patches like real flower meadows
        if (prof && prof.f && nf < MAX_F && ny >= 0.65) {
          const ch = ((Math.floor(gx / 6) * 83492791) ^ (Math.floor(gz / 6) * 297121507)) >>> 0;
          const inCluster = ch % 100 < 22;
          if ((hsh >>> 13) % 100 < prof.f * (inCluster ? 1 : 0.05)) {
            const s = 0.13 + ((hsh >>> 24) % 100) / 100 * 0.1;
            this._Q.setFromAxisAngle(up, (hsh % 53) / 53 * Math.PI);
            this._S.set(s * 1.4, s * edge, s * 1.4);
            this._P.set(x, y - 0.02, z);
            this._M.compose(this._P, this._Q, this._S);
            this.flowers.setMatrixAt(nf, this._M);
            nf++;
          }
        }
        // stone clumps: class-driven, else sparse below / common on high
        // steep ground; none on a face too steep to hold them
        // (S4) a host class's density / size / clump fill follow the measured
        // relief there (rough.js stoneTune; no grid / value = the profile's)
        const tn = prof && prof.r && this.rough ? this._stoneTune(c, x, z) : null;
        const rDens = prof ? (tn ? prof.r * tn.dens : prof.r)
          : (y > 2350 || ny < 0.8 ? (y > 2650 ? 10 : 5) : 1);
        if ((hsh >>> 9) % 100 < rDens && nr < MAX_C && ny >= STONE_MIN_NY) {
          const st = c.st !== undefined ? c.st : this._stoneCell(x, y, z, c, hsh, prof, tn);
          if (st) {
            // the size fades out over the scatter's last 6 m (edge)
            this._S.setScalar(st.s * edge);
            this._P.set(x, y, z);
            this._M.compose(this._P, this._Q.fromArray(st.q), this._S);
            this.rocks.setMatrixAt(nr, this._M);
            this.stoneInst.setXYZW(nr, st.seed, st.count, st.vis, st.tone);
            this.stoneNg.setXYZ(nr, st.n[0], st.n[1], st.n[2]);
            nr++;
          }
        }
      }
    }
    // only the drawn prefix goes up (the slots past count are never read;
    // the buffers are sized at GRASS_CEIL)
    this.mesh.count = n;
    upload(this.mesh.instanceMatrix, n);
    if (this.mesh.instanceColor) upload(this.mesh.instanceColor, n);
    upload(this.tuftVar, n);
    this.bushes.count = nb;
    upload(this.bushes.instanceMatrix, nb);
    upload(this.bushVar, nb);
    this.rocks.count = nr;
    upload(this.rocks.instanceMatrix, nr);
    upload(this.stoneInst, nr);
    upload(this.stoneNg, nr);
    this.flowers.count = nf;
    upload(this.flowers.instanceMatrix, nf);
    this._pendAt = this._pend ? now + RETRY_MS : 0;
  }
}

// a stone clump's cell answer (cached in the cell as c.st): the instance's
// orientation (yaw about the ground normal), scale, seed, how many of its
// stones stand (a satellite where nothing may grow — a way's clearance, a
// house, water — ends the clump there: every stone obeys _grows), the foot's
// sun visibility + the bake's lit normal (surface/shade; else the height
// field's, sun 1). Not cached while a shade / satellite tile is not in.
Grass.prototype._stoneCell = function _stoneCell(x, y, z, c, hsh, prof, tn = null) {
  const seed = (Math.imul(hsh, 0x9e3779b1) >>> 8) & 0xffffff;
  const h = (k) => ((Math.imul(hsh ^ (k * 0x45d9f3b), 0x27d4eb2d) >>> 9) % 1000) / 1000;
  const rc = (prof && prof.rc) || 2;
  const want = Math.min(CLUMP_STONES, 1 + Math.floor(h(1) * (tn ? rc * tn.count : rc)));
  const rs = (prof && prof.rs) || 1;
  const s = (0.5 + h(2) * 0.5) * (tn ? rs * tn.size : rs);
  _qa.setFromUnitVectors(_up, _n.set(c.nx, c.ny, c.nz));
  _qy.setFromAxisAngle(_up, h(3) * 2 * Math.PI);
  _qa.multiply(_qy);
  let ok = true, count = 1;
  for (let k = 1; k < want; k++) {
    const p = stoneCentre(seed, k);
    _v.set(p.x * s, 0, p.z * s).applyQuaternion(_qa);
    const sx = x + _v.x, sz = z + _v.z;
    if (!this.surfaceIn(sx, sz)) { ok = false; break; }
    if (!this._grows(sx, y, sz)) break;
    count = k + 1;
  }
  const S = this.surface, sh = shadeAt(S, x, z);
  if (!sh && S && S.meta && S.meta.shade) ok = false;     // its shade tile is not in yet
  const n = sh ? sh.n : [c.nx, c.ny, c.nz], vis = sh ? sh.vis : 1;
  const out = { q: _qa.toArray(), s, seed, count, vis, n, tone: 0.88 + h(4) * 0.2 };
  if (ok) c.st = out;
  else this._pend = true;
  return out;
};

// (S4) the cell's stone factors from the roughness grid, cached on the cell
// (setRough clears the cells): null off the host classes / without a value
Grass.prototype._stoneTune = function _stoneTune(c, x, z) {
  if (c.tn === undefined) c.tn = stoneTune(this._clsKey.get(c.cid), roughAt(this.rough, x, z));
  return c.tn;
};

// the foot's sun visibility + the bake's lit normal (surface/shade, GROUND-M
// rev 98: R = visibility, G / B = 0.5 + 0.5 n.x / n.z), null without the tile
function shadeAt(S, x, z) {
  const T = S && S.meta && S.meta.tiles, sm = S && S.meta && S.meta.shade;
  if (!T || !sm || !S.tileData || !(T.tile_m > 0)) return null;
  const tx = Math.floor(x / T.tile_m), ty = Math.floor(z / T.tile_m);
  const d = S.tileData(tx, ty);
  if (!d || !d.shade || !(d.spx > 0)) return null;
  const cm = T.tile_m / d.spx;
  const cx = Math.min(d.spx - 1, Math.max(0, Math.floor((x - tx * T.tile_m) / cm)));
  const cz = Math.min(d.spx - 1, Math.max(0, Math.floor((z - ty * T.tile_m) / cm)));
  const o = (cz * d.spx + cx) * 3;
  const nx = d.shade[o + 1] / 127.5 - 1, nz = d.shade[o + 2] / 127.5 - 1;
  return { vis: d.shade[o] / 255, n: [nx, Math.sqrt(Math.max(0, 1 - nx * nx - nz * nz)), nz] };
}
const _qa = new THREE.Quaternion(), _qy = new THREE.Quaternion(), _v = new THREE.Vector3();

const _up = new THREE.Vector3(0, 1, 0);
const CELL_NONE = 0;          // a cached cell where nothing grows
// an instance attribute's first n items up (n 0: nothing to send)
function upload(att, n) {
  att.clearUpdateRanges();
  if (n > 0) { att.addUpdateRange(0, n * att.itemSize); att.needsUpdate = true; }
}
const CELL_OWED = -1;         // not derived yet (the frame's GRASS_COLD_CELLS spent)
const _n = new THREE.Vector3();

function makeTuftGeometry() {
  // two crossed quads, pivot at the base, unit height
  const geo = new THREE.BufferGeometry();
  const pos = [], uv = [], idx = [];
  let vi = 0;
  for (const rot of [0, Math.PI / 2]) {
    const dx = Math.cos(rot) * 0.5, dz = Math.sin(rot) * 0.5;
    pos.push(-dx, 0, -dz, dx, 0, dz, dx, 1, dz, -dx, 1, -dz);
    uv.push(0, 0, 1, 0, 1, 1, 0, 1);
    idx.push(vi, vi + 1, vi + 2, vi, vi + 2, vi + 3);
    vi += 4;
  }
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(idx);
  return geo;
}

// derivative-sharpened alpha test shared by every scatter sprite (tufts, bushes, flowers) — mipmap
// averaging otherwise fades fine blades/needles out at distance. The trees' alpha-to-coverage path
// (vegmat.js vegAlphaGLSL; vr-shimmer, APP_SURFACE_PERF): on a multisampled target the sharpened ramp is
// the sample coverage and from mip ~1 the mip's averaged alpha (the blades' true area, no on / off
// pixels), the alpha writes masked for the draw (vegCoverage, set where each material is made); one
// sample: the old sharpen + discard at 0.5, opaque — the same pixels.
// mat: the material (its vegCoverage uVegMs); own: its vegOwnState when it can carry an owner PNG (bushes)
function sharpenAlpha(shader, mat, own = null, look = true) {
  vegCoverageDecl(shader, mat);
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <alphatest_fragment>', vegAlphaGLSL());
  if (own) vegOwnLook(shader, own);
  else if (look) vegLook(shader);
}

// the bush / flower / tuft painters live in vegpaint.js (shared with the
// dashboard's Vegetation preview); this side only wraps them in textures
export { BUSH_VAR };

// a bush atlas painted in the pool (vegmat.js poolAtlas: S 64 for the
// ankle-height grass garnish; shrubs.js asks 128 because its measured hedges
// stand 0.5-3 m and get walked right up to — the painting is authored in
// 64-px coordinates and scaled: same silhouettes, only crisper) on mat (the
// grass bushes; shrubs.js): mat keeps drawing the atlas it wears until `a` landed,
// then wears `a` (drawn from then on: visible; no column the owner's yet) and
// the old one goes; a newer atlas asked before `a` landed supersedes it. The
// owner's sprites go onto `a` (loadBushSprites; sh = the shrubs block).
export function wearBushAtlas(owner, mat, a, sh) {
  const ud = mat.userData;
  if (ud.atlasWant && ud.atlasWant !== ud.atlas) disposeAtlas(ud.atlasWant);   // superseded before it landed
  ud.atlasWant = a;
  a.ready.then(() => {
    if (ud.atlasWant !== a) return;
    const old = ud.atlas;
    ud.atlas = a;
    mat.map = a.tex;
    ud.vegOwn.value.set(0, 0);
    mat.needsUpdate = true;
    mat.visible = true;
    if (old && old !== a) disposeAtlas(old);
  }, (err) => { if (!a.dead) console.warn('[veg atlas]', err); });
  loadBushSprites(owner, mat, a, owner.baseUrl, sh);
}

// the owner's shrub PNGs: meta.vegetation.shrubs.sprite_leafy / sprite_needle
// (sha16) → <bake>/veg/<sha>.webp (vegSpriteUrl) drawn INSTEAD of atlas column 0 / 1
// (vegpaint.js bushSpriteCell, in the pool: vegmat.js atlasSprite) of atlas
// `a`. Fetched as bulk once per url and kept (the blob), so a repaint
// (applyVegetation) re-applies them without a refetch; the painted cell stands
// until the sprite lands and stays when the bake lacks it.
// Grass and Shrubs share the fetch; owner._spriteGen drops a stale apply.
// mat.userData.ownAsk = the url per column asked for (null: none) — the
// overlay's sprite gate (main.js spritesWorn) waits for exactly those.
const BUSH_SPRITES = ['sprite_leafy', 'sprite_needle'];   // index = atlas column
const _bushSprite = new Map();                           // url → Promise<Blob|null>
export function loadBushSprites(owner, mat, a, baseUrl, sh) {
  const gen = ++owner._spriteGen;
  const own = mat.userData.vegOwn;
  mat.userData.ownAsk = BUSH_SPRITES.map((key) => {
    const sha = sh && sh[key];
    return baseUrl && typeof sha === 'string' && /^[0-9a-f]{16}$/.test(sha) ? vegSpriteUrl(baseUrl, sha) : null;
  });
  mat.userData.ownAsk.forEach((url, v) => {
    if (!url) return;
    let p = _bushSprite.get(url);
    if (!p) {
      p = fetchAsset(url, { prio: 'bulk', as: 'blob' }).catch(() => null);
      _bushSprite.set(url, p);
    }
    p.then(async (b) => {
      if (!b || gen !== owner._spriteGen || a.dead) return;
      if (!(await atlasSprite(a, b, v)) || gen !== owner._spriteGen) return;
      const flip = () => { if (gen === owner._spriteGen && mat.map === a.tex) own.value.setComponent(v, 1); };   // this column now draws in the owner look
      // the owner's picture's own season page (shrubs.js); async: the column flips once its page is bound
      if (mat.userData.sprSeason) setSpritePage(mat, a, BUSH_ALPHA).then(flip, (err) => console.warn('[season page]', err));
      else flip();
    }).catch(() => {});
  });
}

// the tuft's atlas column: reeds on wetland, short turf on dry / sport /
// gravelly ground, meadow grass elsewhere with a share in seed on meadow,
// lawn and piste (deterministic: the cell hash)
function tuftVar(cid, dry, hsh) {
  if (cid === 7) return 2;
  if (cid === 8 || cid === 4 || cid === 5 || cid === 6 || cid === 11 || dry > 0.55) return 1;
  if ((cid === 0 || cid === 9 || cid === 10) && (hsh >>> 26) % 100 < 30) return 3;
  return (hsh >>> 19) % 100 < (cid === 1 ? 45 : 12) ? 1 : 0;
}

// 128-px columns: the tufts stand ankle- to knee-high right at the camera.
// lum (atlas.lum once it landed): per column the alpha-weighted mean linear
// luminance of the painted blades (the shader's blade shading divides by it:
// mean 1), derived in the pool from the painted bytes (pool-veg.js tuftLum)
function makeTuftAtlas(run) {
  return poolAtlas(run, 'tuft', { S: 128, nvar: TUFT_VAR });
}
